import React from "react"
import { isEqual } from "lodash-es"
import * as API from "services/api"
import * as StatusListHelpers from "utils/statusListHelpers"
import { useDispatch, useSelector } from "react-redux"
import {
  closedSubmissionStatusName as closedSubmissionStatusNameSelector,
  submissionStatuses,
} from "reduxSlices/categorySlice"
import { allConversationsClosed as allConversationsClosedSelector } from "reduxSlices/formSubmissionConversationsSlice"
import {
  allFormSubmissionTasksClosed,
  closeSubmission,
  formSubmissionClosed,
  formSubmissionSlug as formSubmissionSlugSelector,
  formSubmissionStatusName,
  setSubmissionStatus,
  submissionPermissions,
} from "reduxSlices/formSubmissionSlice"
import { useFormSubmissionFormContext } from "contexts/FormSubmissionFormContext"
import { errorToast } from "shared/toast"
import { Select } from "shared/selects"
import Tooltip from "shared/Tooltip"
import ClosedSubmissionSelect from "./ClosedSubmissionSelect"

const FormSubmissionStatus = () => {
  const dispatch = useDispatch()

  const { canCloseSubmission: userHasPermissionToCloseFormSubmission } = useSelector(submissionPermissions)

  const formSubmissionIsClosed = useSelector(formSubmissionClosed)
  const currentStatusName = useSelector(formSubmissionStatusName)
  const formSubmissionSlug = useSelector(formSubmissionSlugSelector)

  const allTasksClosed = useSelector(allFormSubmissionTasksClosed)
  const allConversationsClosed = useSelector(allConversationsClosedSelector)

  const allCategoryStatuses = useSelector(submissionStatuses, isEqual)
  const categoryClosedStatusName = useSelector(closedSubmissionStatusNameSelector)

  const { editMode } = useFormSubmissionFormContext()

  if (formSubmissionIsClosed) {
    return (
      userHasPermissionToCloseFormSubmission // Implies permission to reopen
        ? <ClosedSubmissionSelect />
        : <span>{currentStatusName}</span>
    )
  }

  const closeFormSubmission = async (newStatusName) => {
    const response = await API.closeSubmission({ formSubmissionSlug })

    if (response.ok) {
      dispatch(closeSubmission(newStatusName))
    } else {
      errorToast("Something went wrong. Unable to close form submission.")
    }
  }

  const updateFormSubmissionStatus = async (newStatusName) => {
    const response = await API.updateSubmissionStatus({ formSubmissionSlug, statusName: newStatusName })

    if (response.ok) {
      dispatch(setSubmissionStatus(newStatusName))
    } else {
      errorToast("Something went wrong. Unable to change form submission status.")
    }
  }

  const updateStatus = async (newStatusName) => {
    if (newStatusName === currentStatusName) return
    const updateFunction = categoryClosedStatusName === newStatusName ? closeFormSubmission : updateFormSubmissionStatus

    updateFunction(newStatusName)
  }

  const statusListOptions = StatusListHelpers.statusListOptions(
    allCategoryStatuses,
    userHasPermissionToCloseFormSubmission,
    allTasksClosed,
    allConversationsClosed,
  )

  return (
    <Tooltip text={editMode ? "Cannot change status while editing a submission." : ""}>
      <Select
        className="bg-white font-medium text-sm"
        hasBaseInputStyle={false}
        style={{
          minHeight: "34px",
          borderRadius: "4px",
          minWidth: "200px",
          zIndex: "100",
        }}
        disabled={editMode}
        options={statusListOptions}
        value={currentStatusName}
        placeholder="Select status"
        onChange={updateStatus}
        backspaceDelete={false}
        label="submission status"
      />
    </Tooltip>
  )
}

export default FormSubmissionStatus
